import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Spinner } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import AdSlot from "@/components/site/AdSlot";
import { getBlog } from "@/lib/api";
import { Search, ArrowUpRight, Users, ArrowRight, Wrench, ShoppingBag, BookOpen } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [allPosts, setAllPosts] = useState([]);         // used to compute category tabs + counts
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [avenoStories, setAvenoStories] = useState(null);
  const [educationPosts, setEducationPosts] = useState(null);
  const [clientStories, setClientStories] = useState(null);
  const [toolGuides, setToolGuides] = useState(null);

  // Full catalog once — used to build dynamic category tabs with real counts.
  useEffect(() => {
    getBlog().then((all) => setAllPosts(all || [])).catch(() => setAllPosts([]));
  }, []);

  useEffect(() => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (query) params.search = query;
    setPosts(null);
    getBlog(params).then(setPosts).catch(() => setPosts([]));
  }, [cat, query]);

  // Client Stories are always fetched separately (they get their own hero row).
  useEffect(() => {
    getBlog({ category: "Client Stories" }).then(setClientStories).catch(() => setClientStories([]));
    getBlog({ category: "Tools" }).then(setToolGuides).catch(() => setToolGuides([]));
    getBlog({ category: "AVENO" }).then(setAvenoStories).catch(() => setAvenoStories([]));
    getBlog({ category: "Education" }).then(setEducationPosts).catch(() => setEducationPosts([]));
  }, []);

  // Build category list dynamically: pin Client Stories first, then sort the
  // rest by post count (descending). Categories with zero posts are hidden.
  const CATEGORIES = useMemo(() => {
    if (!allPosts.length) return [];
    const counts = new Map();
    allPosts.forEach((p) => {
      const c = p.category || "Uncategorized";
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    const pinned = ["Client Stories", "Tools", "AVENO", "Education"];
    const pinnedFirst = pinned.filter((c) => counts.has(c));
    const rest = Array.from(counts.keys())
      .filter((c) => !pinned.includes(c))
      .sort((a, b) => counts.get(b) - counts.get(a));
    return [
      { key: "All", label: "All", count: allPosts.length },
      ...pinnedFirst.map((c) => ({ key: c, label: c, count: counts.get(c) })),
      ...rest.map((c) => ({ key: c, label: c, count: counts.get(c) })),
    ];
  }, [allPosts]);

  const featured = (posts || []).find((p) => p.featured);
  const rest = (posts || []).filter((p) => p.id !== featured?.id);
  const showClientHero = cat === "All" && !query;

  return (
    <div data-testid="blog-page">
      <PageHeader
        chapter="Blog / Insights"
        titleLines={["Ideas on tech,", "growth & more."]}
        description="Practical articles on web development, SEO, marketing, entrepreneurship, AI, and learning — written to help you grow."
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">

          {/* CLIENT STORIES — dedicated section (always visible under All / no search) */}
          {showClientHero && (
            <Reveal>
              <div className="mb-14 border border-primary/40 rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" data-testid="client-stories-section">
                <div className="p-6 md:p-10">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center">
                          <Users className="w-5 h-5" />
                        </div>
                        <p className="overline text-primary">Client Stories</p>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">How I've helped real businesses grow.</h2>
                      <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
                        Behind-the-scenes case studies of the projects, launches, and turnarounds I've delivered for clients across the world.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCat("Client Stories")}
                      data-testid="client-stories-see-all"
                      className="inline-flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 hover:border-primary hover:text-primary transition"
                    >
                      See all <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="client-stories-grid">
                    {!clientStories ? (
                      <div className="md:col-span-3"><Spinner /></div>
                    ) : clientStories.length === 0 ? (
                      <div className="md:col-span-3 border border-dashed border-border rounded-xl p-8 text-center" data-testid="client-stories-empty">
                        <p className="text-muted-foreground">
                          Client stories are on the way — I'll be publishing case studies for each of my existing clients here soon.
                        </p>
                        <p className="text-xs font-mono uppercase tracking-widest text-primary mt-3">Stay tuned</p>
                      </div>
                    ) : (
                      clientStories.slice(0, 3).map((p) => (
                        <Link key={p.id} to={`/blog/${p.slug}`}
                          data-testid={`client-story-${p.slug}`}
                          className="group border border-border rounded-xl overflow-hidden bg-card hover:border-primary transition-colors">
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                          <div className="p-5">
                            <h3 className="font-display text-lg leading-snug group-hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                            <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                              <span>{p.read_time || "3 min read"}</span>
                              <ArrowUpRight className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* AVENO STORIES — one story per product from the family shop */}
          {showClientHero && (
            <Reveal>
              <div className="mb-14 border border-border rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50/40 to-transparent" data-testid="aveno-stories-section">
                <div className="p-6 md:p-10">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center">
                          <ShoppingBag className="w-5 h-5" />
                        </div>
                        <p className="overline text-primary">AVENO Stories</p>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">The story behind every AVENO product.</h2>
                      <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
                        One story for every piece in the family shop — where it came from, how it fits into everyday life, and where to get it.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCat("AVENO")}
                      data-testid="aveno-stories-see-all"
                      className="inline-flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 hover:border-primary hover:text-primary transition"
                    >
                      See all {avenoStories ? avenoStories.length : ""} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="aveno-stories-grid">
                    {!avenoStories ? (
                      <div className="md:col-span-3"><Spinner /></div>
                    ) : avenoStories.length === 0 ? (
                      <div className="md:col-span-3 border border-dashed border-border rounded-xl p-8 text-center" data-testid="aveno-stories-empty">
                        <p className="text-muted-foreground">Product stories are being crafted — one story per AVENO product, released daily.</p>
                        <p className="text-xs font-mono uppercase tracking-widest text-primary mt-3">Stay tuned</p>
                      </div>
                    ) : (
                      avenoStories.slice(0, 3).map((p) => (
                        <Link key={p.id} to={`/blog/${p.slug}`}
                          data-testid={`aveno-story-${p.slug}`}
                          className="group border border-border rounded-xl overflow-hidden bg-card hover:border-primary transition-colors">
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                          <div className="p-5">
                            <h3 className="font-display text-lg leading-snug group-hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                            <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                              <span>{p.read_time || "4 min read"}</span>
                              <ArrowUpRight className="w-4 h-4 text-primary" />
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* EDUCATION — literacy in secondary schools series */}
          {showClientHero && (
            <Reveal>
              <div className="mb-14 border border-border rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50/40 to-transparent" data-testid="education-section">
                <div className="p-6 md:p-10">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-600/15 text-emerald-700 flex items-center justify-center">
                          <BookOpen className="w-5 h-5" />
                        </div>
                        <p className="overline text-emerald-700">Education</p>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">Every teenager deserves to read well.</h2>
                      <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
                        A warm, honest series on literacy in secondary education — the data, the challenges schools and teachers face, and the strategies that actually move reading rates.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCat("Education")}
                      data-testid="education-see-all"
                      className="inline-flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 hover:border-emerald-600 hover:text-emerald-700 transition"
                    >
                      See all {educationPosts ? educationPosts.length : ""} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="education-grid">
                    {!educationPosts ? (
                      <div className="md:col-span-3"><Spinner /></div>
                    ) : educationPosts.length === 0 ? (
                      <div className="md:col-span-3 border border-dashed border-border rounded-xl p-8 text-center" data-testid="education-empty">
                        <p className="text-muted-foreground">The literacy series is being written — fresh articles arrive every day.</p>
                        <p className="text-xs font-mono uppercase tracking-widest text-emerald-700 mt-3">Stay tuned</p>
                      </div>
                    ) : (
                      educationPosts.slice(0, 3).map((p) => (
                        <Link key={p.id} to={`/blog/${p.slug}`}
                          data-testid={`education-post-${p.slug}`}
                          className="group border border-border rounded-xl overflow-hidden bg-card hover:border-emerald-600 transition-colors">
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                          <div className="p-5">
                            <h3 className="font-display text-lg leading-snug group-hover:text-emerald-700 transition-colors line-clamp-2">{p.title}</h3>
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                            <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                              <span>{p.read_time || "6 min read"}</span>
                              <ArrowUpRight className="w-4 h-4 text-emerald-700" />
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          <div className="flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between mb-10">
            {/* TOOL GUIDES — dedicated section for the free-tools library */}
          {showClientHero && toolGuides && toolGuides.length > 0 && (
            <Reveal>
              <div className="mb-14 border border-border rounded-2xl bg-secondary/30 p-6 md:p-10" data-testid="tool-guides-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Wrench className="w-5 h-5" />
                      </div>
                      <p className="overline text-primary">Tool Guides</p>
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">Every free tool, explained.</h2>
                    <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
                      What each tool does, how to use it, and three to five people who rely on it — a guide for every tool in the library.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCat("Tools")}
                    data-testid="tool-guides-see-all"
                    className="inline-flex items-center gap-2 text-sm border border-border rounded-full px-4 py-2 hover:border-primary hover:text-primary transition"
                  >
                    See all {toolGuides.length} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="tool-guides-grid">
                  {toolGuides.slice(0, 3).map((p) => (
                    <Link key={p.id} to={`/blog/${p.slug}`}
                      data-testid={`tool-guide-${p.slug}`}
                      className="group border border-border rounded-xl overflow-hidden bg-card hover:border-primary transition-colors">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <div className="p-5">
                        <h3 className="font-display text-lg leading-snug group-hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          <span>{p.read_time || "4 min read"}</span>
                          <ArrowUpRight className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <div className="flex flex-wrap gap-2" data-testid="blog-filters">
              {CATEGORIES.map((c) => {
                const active = cat === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCat(c.key)}
                    data-testid={`blog-cat-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground/80 border-border hover:border-primary hover:text-primary"
                    }`}
                  >
                    <span>{c.label}</span>
                    <span className={`inline-flex items-center justify-center rounded-full text-[10px] font-mono min-w-[1.4rem] h-5 px-1.5 ${
                      active ? "bg-white/25 text-white" : "bg-secondary text-muted-foreground"
                    }`}>{c.count}</span>
                  </button>
                );
              })}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); setQuery(search); }} className="flex items-center border border-border rounded-full px-4 lg:w-72">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input data-testid="blog-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles…" className="bg-transparent py-2.5 px-3 outline-none w-full text-sm" />
              {query && (
                <button type="button" onClick={() => { setSearch(""); setQuery(""); }} className="text-xs text-muted-foreground hover:text-primary">Clear</button>
              )}
            </form>
          </div>

          {!posts ? <Spinner /> : posts.length === 0 ? (
            <p className="py-20 text-center text-muted-foreground">No articles found.</p>
          ) : (
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8 space-y-10">
                {featured && cat === "All" && !query && (
                  <Reveal>
                    <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group block border border-border overflow-hidden">
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <span className="absolute top-4 left-4 font-mono text-xs bg-primary text-white px-3 py-1 uppercase tracking-widest">Featured · {featured.category}</span>
                      </div>
                      <div className="p-8">
                        <h2 className="font-display text-3xl md:text-4xl group-hover:text-primary transition-colors">{featured.title}</h2>
                        <p className="text-muted-foreground mt-3 leading-relaxed">{featured.excerpt}</p>
                        <p className="font-mono text-xs text-muted-foreground mt-5">{featured.date} · {featured.read_time}</p>
                      </div>
                    </Link>
                  </Reveal>
                )}
                <div className="grid sm:grid-cols-2 gap-6">
                  {(cat === "All" && !query ? rest : posts).map((p, i) => (
                    <Reveal key={p.id} delay={(i % 2) * 0.06}>
                      <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col h-full border border-border">
                        <div className="relative aspect-video overflow-hidden">
                          <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-3">{p.category}</p>
                          <h3 className="font-display text-xl leading-snug group-hover:text-primary transition-colors">{p.title}</h3>
                          <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                          <div className="mt-5 flex items-center justify-between font-mono text-xs text-muted-foreground">
                            <span>{p.read_time}</span>
                            <ArrowUpRight className="w-4 h-4 text-primary" />
                          </div>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4 space-y-8">
                <div className="border border-border p-6">
                  <p className="overline text-primary mb-4">Browse by category</p>
                  <div className="flex flex-col gap-1">
                    {CATEGORIES.filter((c) => c.key !== "All").map((c) => {
                      const active = cat === c.key;
                      return (
                        <button
                          key={c.key}
                          onClick={() => setCat(c.key)}
                          data-testid={`blog-side-cat-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`flex items-center justify-between px-3 py-2 rounded-md text-sm text-left transition-colors ${
                            active ? "bg-primary/10 text-primary font-medium" : "hover:bg-secondary text-foreground/80"
                          }`}
                        >
                          <span>{c.label}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">{c.count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <AdSlot placement="blog" />

                {/* Related pages so blog readers can hop into related sections
                    without needing them in the main nav. */}
                <div className="border border-border p-6" data-testid="blog-related">
                  <p className="overline text-primary mb-4">More on this site</p>
                  <div className="space-y-3">
                    {[
                      { to: "/writing",     label: "Writing",         desc: "Personal essays." },
                      { to: "/resources",   label: "Free Resources",  desc: "Templates & guides." },
                      { to: "/shop", label: "Shop",     desc: "Tools I use daily." },
                      { to: "/aveno",   label: "AVENO",       desc: "My everyday-goods brand." },
                    ].map(({ to, label, desc }) => (
                      <Link key={to} to={to} data-testid={`blog-related-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="group flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-b-0">
                        <div className="min-w-0">
                          <p className="font-medium group-hover:text-primary transition-colors">{label}</p>
                          <p className="text-xs text-muted-foreground truncate">{desc}</p>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
